import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarClock, Wrench } from "lucide-react";
import { papi, errText } from "../portalApi";
import { Card, EmptyState, Field, Modal, PageHeader, Spinner, StatusBadge, fmtDate, fmtTime, inputCls } from "../ui";

const ISSUE_TYPES = ["Missing Punch", "Forgot Check-Out", "Wrong Time", "System Error", "Other"];
const DAY = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export default function Attendance() {
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [reg, setReg] = useState(null); // { date }
  const [form, setForm] = useState({ issueType: ISSUE_TYPES[0], requestedCheckIn: "", requestedCheckOut: "", reason: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await papi.get("/attendance", { params: { month } });
      setData(r.data);
    } catch (err) {
      toast.error(errText(err, "Could not load attendance."));
    }
  }, [month]);

  useEffect(() => { load(); }, [load]);

  if (!data) return <Spinner />;

  const holidayMap = Object.fromEntries(data.holidays.map((h) => [h.date, h]));
  const today = new Date().toISOString().slice(0, 10);
  const rows = [...data.records].sort((a, b) => (a.date < b.date ? 1 : -1));
  const s = data.summary;

  const openReg = (date) => {
    setForm({ issueType: ISSUE_TYPES[0], session: 1, requestedCheckIn: "", requestedCheckOut: "", reason: "" });
    setReg({ date });
  };

  const openAnyDay = () => {
    setForm({ issueType: ISSUE_TYPES[0], session: 1, requestedCheckIn: "", requestedCheckOut: "", reason: "" });
    setReg({ date: today, pickDate: true });
  };

  const submitReg = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await papi.post("/attendance/regularise", { date: reg.date, ...form });
      toast.success("Regularisation request submitted for approval.");
      setReg(null);
      load();
    } catch (err) {
      toast.error(errText(err, "Could not submit the request."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="attendance-page">
      <PageHeader title="Attendance" sub="Your monthly attendance record and regularisation requests.">
        <div className="flex items-center gap-2">
          <button onClick={openAnyDay} className="rounded-lg border border-navy-900/15 bg-white px-3 py-2 text-xs font-semibold text-navy-900 transition-colors hover:border-gold" data-testid="regularise-any-day-btn">
            Regularise a Day
          </button>
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className={inputCls}
            style={{ width: "auto" }}
            data-testid="attendance-month-picker"
            aria-label="Select month"
          />
        </div>
      </PageHeader>

      <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-4 lg:grid-cols-7">
        {[
          ["Working Days", s.workingDays, "summary-working-days"],
          ["Present", s.present, "summary-present"],
          ["WFH", s.wfh, "summary-wfh"],
          ["Leave", s.leave, "summary-leave"],
          ["Holidays", s.holidays, "summary-holidays"],
          ["Total Hours", s.totalHours, "summary-total-hours"],
          ["Attendance", `${s.percentage}%`, "summary-percentage"],
        ].map(([label, val, tid]) => (
          <Card key={tid} testid={tid} className="!p-4 text-center">
            <p className="font-serif text-2xl text-navy-900">{val}</p>
            <p className="mt-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>
          </Card>
        ))}
      </div>

      {rows.length === 0 ? (
        <EmptyState icon={CalendarClock} title="No attendance records" sub="Records will appear here once you start checking in." testid="attendance-empty" />
      ) : (
        <Card className="!p-0 overflow-hidden" testid="attendance-table-card">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="attendance-table">
              <thead>
                <tr className="border-b border-navy-900/10 bg-ivory text-left">
                  {["Date", "Day", "Status", "Check In", "Check Out", "Hours", ""].map((h) => (
                    <th key={h} className="px-5 py-3.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const hol = holidayMap[r.date];
                  const day = DAY[new Date(`${r.date}T00:00:00`).getDay()];
                  const displayStatus = hol && !r.checkIn ? "Holiday" : r.status;
                  return (
                    <tr key={r.date} className="border-b border-navy-900/5 last:border-0 hover:bg-ivory/60" data-testid={`attendance-row-${r.date}`}>
                      <td className="px-5 py-3 font-semibold text-navy-900">{fmtDate(r.date)}</td>
                      <td className="px-5 py-3 text-slate-500">{day}</td>
                      <td className="px-5 py-3">
                        <StatusBadge status={displayStatus} />
                        {hol && <p className="mt-1 text-[10px] text-gold-dark">{hol.name}</p>}
                        {r.regularised && <p className="mt-1 text-[10px] font-semibold text-sky-700">Regularised</p>}
                      </td>
                      <td className="px-5 py-3 text-slate-600">
                        {r.checkIn ? fmtTime(r.checkIn) : "—"}
                        {r.checkIn2 && <span className="block text-xs text-slate-400" data-testid={`shift2-in-${r.date}`}>S2: {fmtTime(r.checkIn2)}</span>}
                      </td>
                      <td className="px-5 py-3 text-slate-600">
                        {r.checkOut ? fmtTime(r.checkOut) : "—"}
                        {r.checkOut2 && <span className="block text-xs text-slate-400" data-testid={`shift2-out-${r.date}`}>S2: {fmtTime(r.checkOut2)}</span>}
                      </td>
                      <td className="px-5 py-3 font-semibold text-navy-900">{r.totalHours || "—"}</td>
                      <td className="px-5 py-3 text-right">
                        {r.date <= today && displayStatus !== "Holiday" && (
                          <button
                            onClick={() => openReg(r.date)}
                            className="text-xs font-semibold text-gold-dark transition-colors hover:text-navy-900"
                            data-testid={`regularise-btn-${r.date}`}
                          >
                            Regularise
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Modal open={Boolean(reg)} onClose={() => setReg(null)} title={`Regularise Attendance — ${reg ? fmtDate(reg.date) : ""}`}>
        <form onSubmit={submitReg} className="space-y-4" data-testid="regularise-form">
          {reg?.pickDate && (
            <Field label="Date">
              <input type="date" max={today} className={inputCls} value={reg.date} onChange={(e) => e.target.value && setReg({ ...reg, date: e.target.value })} data-testid="regularise-date" />
            </Field>
          )}
          <Field label="Issue Type">
            <select className={inputCls} value={form.issueType} onChange={(e) => setForm({ ...form, issueType: e.target.value })} data-testid="regularise-issue-type">
              {ISSUE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </Field>
          {data.split && (
            <Field label="Shift">
              <select className={inputCls} value={form.session} onChange={(e) => setForm({ ...form, session: Number(e.target.value) })} data-testid="regularise-session">
                <option value={1}>Shift 1 ({data.shift1Label})</option>
                <option value={2}>Shift 2 ({data.shift2Label})</option>
              </select>
            </Field>
          )}
          <div className="grid grid-cols-2 gap-4">
            <Field label="Correct Check-In">
              <input type="time" className={inputCls} value={form.requestedCheckIn} onChange={(e) => setForm({ ...form, requestedCheckIn: e.target.value })} data-testid="regularise-checkin" />
            </Field>
            <Field label="Correct Check-Out">
              <input type="time" className={inputCls} value={form.requestedCheckOut} onChange={(e) => setForm({ ...form, requestedCheckOut: e.target.value })} data-testid="regularise-checkout" />
            </Field>
          </div>
          <Field label="Reason">
            <textarea required rows={3} className={inputCls} placeholder="Briefly explain what happened" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="regularise-reason" />
          </Field>
          <button type="submit" disabled={saving} className="btn-gold w-full justify-center disabled:opacity-60" data-testid="regularise-submit">
            <Wrench className="h-4 w-4" aria-hidden /> {saving ? "Submitting…" : "Submit for Approval"}
          </button>
        </form>
      </Modal>
    </div>
  );
}
